import React, { Fragment, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  Container,
  Grid,
  CircularProgress,
  Backdrop
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon
} from "@mui/icons-material";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../../variables/api";

const MyForms = (props) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BASE_URL}/surveys/getallresponsesforspecificsurvey.php`)
      .then((res) => {
        if (res.data && res.data.data) {
          setSurveys(res.data.data);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
      });
  }, [id, props.pending]);

  const handleView = (responseId) => {
    navigate(`/Response/${responseId}`);
  };

  const handleDelete = (responseId) => {
    Swal.fire({
      title: "Delete Response?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    }).then((result) => {
      if (result.isConfirmed) {
        axios
          .post(`${BASE_URL}/surveys/deleteresponse.php`, { id: responseId })
          .then((res) => {
            Swal.fire("Deleted!", "Response has been deleted.", "success");
            if (props.setPending) props.setPending(!props.pending);
          })
          .catch(err => {
            Swal.fire("Error", "Failed to delete response", "error");
          });
      }
    });
  };

  // Filter logic
  const filteredResponses = surveys.filter((m) => {
    if (id === ":id") return true; // Show all if id is placeholder
    return m.survey_id === id;
  });

  const displayResponses = id === ":id" ? surveys : filteredResponses;

  return (
    <Box sx={{ p: { xs: 0, sm: 1 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Loading Overlay */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress color="inherit" />
          <Typography variant="h6">Loading Responses...</Typography>
        </Box>
      </Backdrop>

      <Button
        component={Link}
        to="/"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 3 }}
        variant="outlined"
      >
        Back to Dashboard
      </Button>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
           <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
             <Typography variant="h4" component="h1" color="textPrimary">
               {id === ":id" ? "All Responses" : "Survey Responses"}
             </Typography>
           </Box>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'primary.main', color: 'white' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h2" component="div" sx={{ fontWeight: 'bold' }}>
                {displayResponses.length}
              </Typography>
              <Typography variant="subtitle1">
                Total Responses
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} aria-label="responses table">
          <TableHead sx={{ bgcolor: 'grey.100' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Survey Name</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Answered By</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Owner</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayResponses.length > 0 ? (
              displayResponses.map((row, index) => (
                <TableRow
                  key={row.response_id || index}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: 'action.hover' } }}
                >
                  <TableCell component="th" scope="row">
                    {row.survey_name}
                  </TableCell>
                  <TableCell>{row.answeredby}</TableCell>
                  <TableCell>{row.owner}</TableCell>
                  <TableCell>{row.date}</TableCell>
                  <TableCell>
                    <Chip label="Submitted" color="success" size="small" variant="outlined" />
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="View Response">
                      <IconButton color="primary" onClick={() => handleView(row.response_id)}>
                        <VisibilityIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Response">
                      <IconButton color="error" onClick={() => handleDelete(row.response_id)}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                  <Typography variant="body1" color="textSecondary">
                    No responses found.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default MyForms;
